import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import MuiLink from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  BackIcon,
  CertificateIcon,
  CheckCircleIcon,
  ClockIcon,
  HubIcon,
  ImageIcon,
  LiveClassIcon,
  LocationIcon,
  ScheduleIcon,
  StudentIcon,
  UserIcon,
} from 'src/v3/core/icons'
import { getS3ImageFilePath } from 'src/v3/utilities/image-upload'
import type { EventType } from 'src/v3/core/types'
import { accreditationDict } from 'src/v3/events/accreditations'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import CardSectionHeader from 'src/v3/components/card-section-header'
import {
  fetchAccreditations,
  fetchEventAccreditations,
  fetchEventProgress,
  fetchEvents,
  fetchInstructors,
  fetchLinkedServices,
  fetchSessions,
  fetchSyllabus,
  selectAccreditationsByTypeByEvent,
  selectAccreditationsStatus,
  selectEventAccreditationsStatus,
  selectCompletedSessionIDsByEventId,
  selectEventAttention,
  selectLatestAttendedSessionByEventId,
  selectEventPhase,
  selectEventProgress,
  selectEventProgressStatus,
  selectEventsStatus,
  selectShowEventCertificate,
  selectEventV2ById,
  selectInstructors,
  selectInstructorsStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectRegisteredSessionsByEventId,
  selectSessionsByEventId,
  selectSessionsStatus,
  selectSyllabusByEventId,
} from './events-v2.slice'
import {
  isJoinWindowOpen,
  isSessionPast,
  isStartingSoon,
} from './utils/access-window'
import { normalizeUrl, transformEventState } from './utils/transforms'
import { useFreshFetch } from './hooks/use-fresh-fetch'
import AttendanceCard from './components/attendance-card'
import CertificateCard from './components/certificate-card'
import QuizCard from './components/quiz-card'
import SessionsPanel from './components/sessions-panel'
import type { SessionAttention } from './components/session-row'
import type { EventQuizProgress } from './types'
import { attentionLabel } from './utils/session-attention'
import SurveyCard from './components/survey-card'

dayjs.extend(utc)
dayjs.extend(timezone)

let LOCAL_TZ = dayjs.tz.guess()

let deliveryIcon = (location: EventType) => {
  if (location.kind === 'virtual') return LiveClassIcon
  if (location.kind === 'inPerson') return LocationIcon
  return HubIcon
}

let getAddress = (location: EventType): string | null => {
  if (location.kind === 'virtual') return null
  return [
    location.address,
    location.city,
    [transformEventState(location.state), location.zip]
      .filter(Boolean)
      .join(' '),
  ]
    .filter(Boolean)
    .join(', ')
}

let getVirtualUrl = (location: EventType): string | null => {
  if (location.kind === 'inPerson') return null
  return location.url ?? null
}

let EventDetailV2Page = () => {
  let { eventId = '' } = useParams<{ eventId: string }>()
  useScrollToTop(undefined, [eventId])
  let navigate = useNavigate()

  let dispatch = useAppDispatch()
  let { showSuccess, showError, NotificationComponent } = useNotifications()

  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let eventsStatus = useAppSelector(selectEventsStatus)
  let sessionsStatus = useAppSelector(selectSessionsStatus)
  let instructorsStatus = useAppSelector(selectInstructorsStatus)
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let eventAccreditationsStatus = useAppSelector(
    selectEventAccreditationsStatus,
  )

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(eventsStatus, fetchEvents)
  useAutoFetch(sessionsStatus, fetchSessions)
  useAutoFetch(instructorsStatus, fetchInstructors)
  useAutoFetch(accreditationsStatus, fetchAccreditations)
  useAutoFetch(eventAccreditationsStatus, fetchEventAccreditations)

  let progressStatus = useAppSelector(selectEventProgressStatus(eventId))
  let phase = useAppSelector(selectEventPhase(eventId))
  let showCertificate = useAppSelector(selectShowEventCertificate(eventId))
  // Always re-fetch on mount so returning from the quiz page reflects the latest progress.
  // mountProgressDone gates the phase card so cached Redux state never causes it to render
  // before the fresh /progress response arrives.
  let mountProgressDone = useFreshFetch(
    eventId,
    progressStatus,
    fetchEventProgress,
  )

  let event = useAppSelector(selectEventV2ById(eventId))
  let sessions = useAppSelector(selectSessionsByEventId(eventId))
  let registeredSessions = useAppSelector(
    selectRegisteredSessionsByEventId(eventId),
  )
  let accreditationsByType = useAppSelector(
    selectAccreditationsByTypeByEvent(eventId),
  )
  let linkedServices = useAppSelector(selectLinkedServices)
  let instructors = useAppSelector(selectInstructors)
  let progress = useAppSelector(selectEventProgress(eventId))
  let syllabus = useAppSelector(selectSyllabusByEventId(eventId))
  let attendedSession = useAppSelector(
    selectLatestAttendedSessionByEventId(eventId),
  )
  let eventAttention = useAppSelector(selectEventAttention(eventId))
  let completedSessionIDs = useAppSelector(
    selectCompletedSessionIDsByEventId(eventId),
  )
  let [surveyOpen, setSurveyOpen] = useState(false)
  let [attendanceOpen, setAttendanceOpen] = useState(false)
  let attendedSessionDisplayTime = attendedSession
    ? dayjs
        .utc(attendedSession.sessionTime)
        .tz(LOCAL_TZ)
        .format('ddd, MMM D · h:mm A z')
    : undefined

  useEffect(() => {
    if (
      eventsStatus === 'fulfilled' &&
      event != null &&
      !event.linkedServiceID
    ) {
      dispatch(fetchSyllabus(eventId))
    }
  }, [dispatch, eventId, eventsStatus])

  if (eventsStatus === 'fulfilled' && !event) {
    navigate('/events/v2', { replace: true })
    return null
  }

  let orgName = event?.linkedServiceID
    ? (linkedServices.find((s) => s.id === event?.linkedServiceID)?.name ??
      null)
    : null

  let capceCode = accreditationsByType.capce[0]?.courseCode ?? null
  let anccExpiration = accreditationsByType.ancc[0]?.expirationDate ?? null
  let hasQuiz = progress != null && progress.quiz.kind !== 'notRequired'

  let upcomingCount = sessions.filter(
    (s) => !isSessionPast(s.sessionTime, s.endTime),
  ).length

  let startingSoonSession = sessions.find(
    (s) =>
      isStartingSoon(s.sessionTime) &&
      s.invites.some((i) => i.status === 'accepted' || i.status === 'invited'),
  )

  let allAccreditations = Object.values(accreditationsByType).flat()
  let displayableAccreditations = allAccreditations

  let DeliveryIcon = event ? deliveryIcon(event.location) : null

  let renderPhaseCard = (p: typeof phase) => {
    if (!p) return null
    if (p.phase === 'attendance')
      return (
        <AttendanceCard
          sessionID={p.sessionID}
          sessionName={p.sessionName}
          sessionTime={p.sessionTime}
          mechanism={p.mechanism}
          selfCheckInOpen={p.selfCheckInOpen}
          onSuccess={showSuccess}
          onError={showError}
          onRecorded={() => {
            dispatch(fetchEventProgress(eventId))
            // Check-in moves the invite to Attended, so the session list is stale too.
            dispatch(fetchSessions())
          }}
          onCheckAgain={() => dispatch(fetchEventProgress(eventId))}
          dialogOpen={attendanceOpen}
          onDialogOpenChange={setAttendanceOpen}
        />
      )
    if (p.phase === 'quiz')
      return (
        <QuizCard
          eventId={eventId}
          hours={event?.hours ?? 0}
          sessionName={attendedSession?.name}
          sessionDisplayTime={attendedSessionDisplayTime}
        />
      )
    if (p.phase === 'survey')
      return (
        <SurveyCard
          eventId={eventId}
          hours={event?.hours ?? 0}
          onSuccess={showSuccess}
          onError={showError}
          sessionName={attendedSession?.name}
          sessionDisplayTime={attendedSessionDisplayTime}
          dialogOpen={surveyOpen}
          onDialogOpenChange={setSurveyOpen}
        />
      )
    return null
  }

  // The one session that needs something from the user, refined with fresh /progress (which
  // the events payload can lag). Attendance comes straight from /progress, which names the
  // session; quiz/survey need the events payload's incompleteSessionID to know which session
  // the attempt's credit goes to. Gated on fresh progress so cached state never shows a stale step.
  let attentionFor = (
    p: typeof phase,
  ): (SessionAttention & { sessionId: string }) | null => {
    if (!mountProgressDone || !p || !progress) return null
    if (p.phase === 'attendance') {
      let canSelfCheckIn =
        p.mechanism.kind === 'quizPasscode' && p.selfCheckInOpen
      return {
        sessionId: p.sessionID,
        label: attentionLabel({
          kind: 'attendance',
          sessionId: p.sessionID,
          mechanism: p.mechanism.kind,
          selfCheckInOpen: p.selfCheckInOpen,
        }),
        action: canSelfCheckIn
          ? {
              label: 'Record attendance',
              onClick: () => setAttendanceOpen(true),
            }
          : undefined,
      }
    }
    if (eventAttention?.kind !== 'requirements') return null
    let { sessionId } = eventAttention
    if (p.phase === 'survey')
      return {
        sessionId,
        label: 'Survey required for completion',
        action: {
          label: 'Complete survey',
          onClick: () => setSurveyOpen(true),
        },
      }
    if (p.phase !== 'quiz') return null
    let quizLabels: Partial<
      Record<EventQuizProgress['kind'], { label: string; action: string }>
    > = {
      notAttempted: {
        label: 'Quiz required for completion',
        action: 'Start quiz',
      },
      inProgress: { label: 'Quiz in progress', action: 'Resume quiz' },
      failed: { label: 'Quiz not passed', action: 'Retake quiz' },
    }
    let labels = quizLabels[progress.quiz.kind]
    if (!labels) return null
    return {
      sessionId,
      label: labels.label,
      action: {
        label: labels.action,
        onClick: () => navigate(`/events/v2/${eventId}/quiz`),
      },
    }
  }
  let attention = attentionFor(phase)

  return (
    <Box component="main" className="pb-[84px]">
      {/* Hero */}
      <Box
        className="relative flex items-center justify-center overflow-hidden border-b"
        sx={{
          height: 340,
          bgcolor: 'background.paperDark',
          borderColor: 'divider',
        }}
      >
        {event?.banner ? (
          <img
            src={getS3ImageFilePath(event.banner)}
            alt=""
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <ImageIcon sx={{ fontSize: 40, color: 'action.disabled' }} />
        )}
        <Box
          className="absolute inset-0"
          sx={{
            background:
              'linear-gradient(to top, rgba(0,0,0,.92) 0%, rgba(0,0,0,.55) 42%, rgba(0,0,0,.15) 100%)',
          }}
        />
        <Box className="absolute flex flex-col gap-3 left-10 right-10 bottom-7">
          {orgName && (
            <Typography
              className="uppercase"
              sx={{
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: '.08em',
                color: 'text.secondary',
              }}
            >
              {orgName}
            </Typography>
          )}
          {event && (
            <>
              <Box className="flex flex-col gap-1.5">
                <Typography
                  component="h1"
                  sx={{
                    fontSize: 34,
                    fontWeight: 500,
                    lineHeight: 1.15,
                    letterSpacing: '-.5px',
                  }}
                >
                  {event.name}
                </Typography>
                <Box className="flex items-center gap-1.5">
                  <StudentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                    {event.categoryName}
                  </Typography>
                </Box>
              </Box>
              <Box className="flex flex-wrap items-center gap-5">
                {DeliveryIcon && (
                  <Box className="flex items-center gap-1.5">
                    <DeliveryIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                    />
                    <Typography sx={{ fontSize: 14 }}>
                      {transformEventState(event.eventType)}
                    </Typography>
                  </Box>
                )}
                <Box className="flex items-center gap-1.5">
                  <CertificateIcon
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Typography sx={{ fontSize: 14 }}>
                    {event.hours} CE hrs
                  </Typography>
                </Box>
              </Box>
              <Box className="flex flex-wrap items-center gap-4">
                <Box className="flex items-center gap-1.5">
                  <ClockIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: 14 }}>
                    {upcomingCount} upcoming session
                    {upcomingCount !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                {startingSoonSession && (
                  <Box className="inline-flex items-center gap-1.5">
                    <ScheduleIcon
                      sx={{ fontSize: 16, color: 'secondary.main' }}
                    />
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'secondary.main',
                      }}
                    >
                      {startingSoonSession.name} starting soon!
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Two-column content */}
      <Container maxWidth="xl" className="py-8">
        <Box className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left column */}
          <Box className="flex flex-col gap-6 lg:col-span-2">
            {/* About the event */}
            <Paper className="p-6">
              <CardSectionHeader label="About the event" className="mb-4" />
              <Typography sx={{ fontSize: 15, lineHeight: 1.65 }}>
                {syllabus?.text || event?.description}
              </Typography>
            </Paper>

            {/* Learning Objectives */}
            {!event?.linkedServiceID &&
              syllabus?.objectives != null &&
              syllabus.objectives.length > 0 && (
                <Paper className="p-6">
                  <CardSectionHeader
                    label="Learning Objectives"
                    className="mb-4"
                  />
                  <List
                    component="ol"
                    disablePadding
                    sx={{ listStyleType: 'decimal', pl: 2.5 }}
                  >
                    {syllabus.objectives
                      .toSorted((a, b) => a.sequence - b.sequence)
                      .map((obj) => (
                        <ListItem
                          key={obj.id}
                          component="li"
                          disablePadding
                          sx={{ display: 'list-item', pl: 0.5, pb: 0.5 }}
                        >
                          <Typography sx={{ fontSize: 15, lineHeight: 1.65 }}>
                            {obj.text}
                          </Typography>
                        </ListItem>
                      ))}
                  </List>
                </Paper>
              )}

            {/* Sessions */}
            <SessionsPanel
              sessions={sessions}
              status={
                sessionsStatus === 'rejected' || eventsStatus === 'rejected'
                  ? 'rejected'
                  : sessionsStatus === 'fulfilled' &&
                      eventsStatus === 'fulfilled'
                    ? 'fulfilled'
                    : sessionsStatus === 'pending' || eventsStatus === 'pending'
                      ? 'pending'
                      : 'uninitialized'
              }
              attention={attention}
              completedSessionIDs={completedSessionIDs}
              onRetry={() => {
                dispatch(fetchSessions())
                dispatch(fetchEvents())
              }}
              onSuccess={showSuccess}
              onError={showError}
            />

            {/* FOAMfrat disclaimer / Information */}
            {event != null &&
              !event.linkedServiceID &&
              (capceCode || anccExpiration || hasQuiz) && (
                <Paper className="p-6">
                  <CardSectionHeader label="Information" className="mb-4" />
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                  >
                    {(capceCode || anccExpiration) && (
                      <Box>
                        <Typography
                          component="h3"
                          variant="subtitle2"
                          sx={{ mb: 1 }}
                        >
                          Course Accreditations
                        </Typography>
                        <List
                          component="ul"
                          disablePadding
                          sx={{ listStyleType: 'disc', pl: 2.5 }}
                        >
                          {capceCode && (
                            <ListItem
                              component="li"
                              disablePadding
                              sx={{
                                display: 'list-item',
                                pl: 0.5,
                                pb: 0.5,
                              }}
                            >
                              <Typography
                                sx={{ fontSize: 14, lineHeight: 1.65 }}
                              >
                                This CE activity is accredited by the Commission
                                of Accreditation for Prehospital Continuing
                                Education for ({event.hours}) Based/Advanced
                                CEH.
                              </Typography>
                            </ListItem>
                          )}
                          {anccExpiration && (
                            <ListItem
                              component="li"
                              disablePadding
                              sx={{ display: 'list-item', pl: 0.5 }}
                            >
                              <Typography
                                sx={{ fontSize: 14, lineHeight: 1.65 }}
                              >
                                FOAMfrat is accredited as a provider of nursing
                                continuing professional development by the
                                American Nurses Credentialing Center's
                                Commission on Accreditation.
                              </Typography>
                            </ListItem>
                          )}
                        </List>
                      </Box>
                    )}
                    <Box>
                      <Typography
                        component="h3"
                        variant="subtitle2"
                        sx={{ mb: 1 }}
                      >
                        Criteria for Receiving Contact Hours
                      </Typography>
                      <List
                        component="ul"
                        disablePadding
                        sx={{ listStyleType: 'disc', pl: 2.5 }}
                      >
                        <ListItem
                          component="li"
                          disablePadding
                          sx={{ display: 'list-item', pl: 0.5, pb: 0.5 }}
                        >
                          <Typography sx={{ fontSize: 14, lineHeight: 1.65 }}>
                            Attendance for 100% of the activity
                          </Typography>
                        </ListItem>
                        {hasQuiz && (
                          <ListItem
                            component="li"
                            disablePadding
                            sx={{ display: 'list-item', pl: 0.5, pb: 0.5 }}
                          >
                            <Typography sx={{ fontSize: 14, lineHeight: 1.65 }}>
                              Successful completion of a post-course quiz
                            </Typography>
                          </ListItem>
                        )}
                        <ListItem
                          component="li"
                          disablePadding
                          sx={{ display: 'list-item', pl: 0.5 }}
                        >
                          <Typography sx={{ fontSize: 14, lineHeight: 1.65 }}>
                            Completion and submission of post-course evaluation
                            form
                          </Typography>
                        </ListItem>
                      </List>
                    </Box>
                    {anccExpiration && (
                      <Box>
                        <Typography
                          component="h3"
                          variant="subtitle2"
                          sx={{ mb: 0.5 }}
                        >
                          ANCC Expiration Date
                        </Typography>
                        <Typography sx={{ fontSize: 14, lineHeight: 1.65 }}>
                          {dayjs(anccExpiration).format('MMMM DD, YYYY')}
                        </Typography>
                      </Box>
                    )}
                    {capceCode && (
                      <Box>
                        <Typography
                          component="h3"
                          variant="subtitle2"
                          sx={{ mb: 0.5 }}
                        >
                          Relationship Disclosure
                        </Typography>
                        <Typography sx={{ fontSize: 14, lineHeight: 1.65 }}>
                          None of the planners or presenters for this activity
                          have relevant financial relationship(s) to disclose
                          with ineligible companies.
                        </Typography>
                      </Box>
                    )}
                    {capceCode && (
                      <Box>
                        <Typography
                          component="h3"
                          variant="subtitle2"
                          sx={{ mb: 0.5 }}
                        >
                          Contact Information
                        </Typography>
                        <Typography sx={{ fontSize: 14, lineHeight: 1.65 }}>
                          For inquiries or support, please contact FOAMfrat LLC
                          at support@foamfrat.com or visit our website at
                          www.foamfrat.com.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              )}
          </Box>

          {/* Right sidebar */}
          <Box className="flex flex-col gap-4">
            {!mountProgressDone && progressStatus !== 'rejected' && (
              <Skeleton variant="rounded" height={120} />
            )}
            {progressStatus === 'rejected' && (
              <Paper className="flex flex-col gap-3 p-6">
                <Typography variant="body2" color="text.secondary">
                  Could not load your progress for this event.
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => dispatch(fetchEventProgress(eventId))}
                >
                  Retry
                </Button>
              </Paper>
            )}
            {mountProgressDone && renderPhaseCard(phase)}
            {mountProgressDone && showCertificate && (
              <CertificateCard
                eventId={eventId}
                eventName={event?.name ?? ''}
                isLinkedService={event == null || !!event.linkedServiceID}
                onError={showError}
                sessionName={attendedSession?.name}
                sessionDisplayTime={attendedSessionDisplayTime}
              />
            )}
            {/* Session registrations */}
            {registeredSessions.length > 0 && event && (
              <Paper className="p-6">
                <CardSectionHeader
                  label="Session registrations"
                  className="mb-4"
                />
                <List component="ul" disablePadding>
                  {registeredSessions.map((s, i) => {
                    let displayTime = dayjs.utc(s.sessionTime).tz(LOCAL_TZ)
                    let joinOpen = isJoinWindowOpen(s.sessionTime, s.endTime)
                    let address = getAddress(event.location)
                    let virtualUrl = getVirtualUrl(event.location)
                    let instructorIDs = s.instructorIDs?.length
                      ? s.instructorIDs
                      : (event.instructorIDs ?? [])
                    let instructorNames = instructors
                      .filter((inst) => instructorIDs.includes(inst.id))
                      .map((inst) => inst.name)
                      .join(', ')

                    return (
                      <ListItem
                        component="li"
                        key={s.id}
                        disablePadding
                        className="flex items-start gap-2"
                        sx={{
                          borderBottom:
                            i < registeredSessions.length - 1
                              ? '1px solid'
                              : 'none',
                          borderColor: 'divider',
                          pb: i < registeredSessions.length - 1 ? 1.5 : 0,
                          pt: i > 0 ? 1.5 : 0,
                        }}
                      >
                        <CheckCircleIcon
                          className="shrink-0 mt-0.5"
                          sx={{ fontSize: 18, color: 'success.main' }}
                        />
                        <Box className="flex-1 min-w-0">
                          <Typography
                            sx={{ fontSize: 12, color: 'text.secondary' }}
                          >
                            {displayTime.format('ddd, MMM D · h:mm A')}{' '}
                            {displayTime.format('z')}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: 'success.main',
                              lineHeight: 1.4,
                            }}
                          >
                            {s.name}
                          </Typography>
                          <Box className="flex flex-col gap-1 mt-1">
                            {instructorNames && (
                              <Box className="flex items-center gap-1.5">
                                <UserIcon
                                  className="shrink-0"
                                  sx={{ fontSize: 14, color: 'text.disabled' }}
                                />
                                <Typography
                                  sx={{ fontSize: 13, color: 'text.secondary' }}
                                >
                                  {instructorNames}
                                </Typography>
                              </Box>
                            )}
                            {address && (
                              <Box className="flex items-start gap-1.5">
                                <LocationIcon
                                  className="shrink-0 mt-0.5"
                                  sx={{ fontSize: 14, color: 'text.disabled' }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    color: 'text.secondary',
                                  }}
                                >
                                  {address}
                                </Typography>
                              </Box>
                            )}
                            {virtualUrl != null && (
                              <Box className="flex items-start gap-1.5">
                                <LiveClassIcon
                                  className="shrink-0"
                                  sx={{ fontSize: 14, color: 'text.disabled' }}
                                />
                                {joinOpen ? (
                                  <MuiLink
                                    href={normalizeUrl(virtualUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ fontSize: 13, fontWeight: 500 }}
                                  >
                                    Join session
                                  </MuiLink>
                                ) : (
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      color: 'text.disabled',
                                    }}
                                  >
                                    Link will appear here 10 minutes prior to
                                    session start
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                    )
                  })}
                </List>
              </Paper>
            )}

            {/* Accreditations */}
            {displayableAccreditations.length > 0 && (
              <Paper className="p-6">
                <CardSectionHeader label="Accreditations" className="mb-4" />
                <List component="ul" disablePadding>
                  {displayableAccreditations.map((a, i) => (
                    <ListItem
                      component="li"
                      key={a.id}
                      disablePadding
                      sx={{
                        borderTop: i > 0 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        pt: i > 0 ? 1.5 : 0,
                        pb: i < displayableAccreditations.length - 1 ? 1.5 : 0,
                      }}
                    >
                      <Box className="flex flex-wrap items-center w-full gap-2">
                        <Chip
                          size="small"
                          label={
                            a.kind === 'notAccredited'
                              ? 'Not Accredited'
                              : 'Accredited'
                          }
                          sx={{
                            bgcolor:
                              a.kind === 'notAccredited'
                                ? 'rgba(255,255,255,.08)'
                                : 'rgba(102,187,106,.16)',
                            color:
                              a.kind === 'notAccredited'
                                ? 'text.secondary'
                                : 'success.main',
                            borderRadius: 1,
                            height: 22,
                            fontSize: 11,
                            fontWeight: 500,
                            letterSpacing: '.06em',
                            textTransform: 'uppercase',
                          }}
                        />
                        {a.kind !== 'accredited' &&
                          a.kind !== 'notAccredited' && (
                            <Typography sx={{ fontSize: 14 }}>
                              {accreditationDict[a.kind]}
                            </Typography>
                          )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* References */}
            {!event?.linkedServiceID &&
              syllabus?.references != null &&
              syllabus.references.length > 0 && (
                <Paper className="p-6">
                  <CardSectionHeader label="References" className="mb-4" />
                  <List
                    component="ol"
                    disablePadding
                    sx={{ listStyleType: 'decimal', pl: 2.5 }}
                  >
                    {syllabus.references.map((ref) => (
                      <ListItem
                        key={ref.id}
                        component="li"
                        disablePadding
                        sx={{ display: 'list-item', pl: 0.5, pb: 0.5 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            lineHeight: 1.6,
                            color: 'text.secondary',
                          }}
                        >
                          {ref.text}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
          </Box>
        </Box>
      </Container>

      {NotificationComponent}

      <Box
        className="fixed bottom-0 left-0 right-0 flex items-center justify-end gap-3 px-8 py-4 border-t"
        sx={{
          zIndex: (theme) => theme.zIndex.appBar,
          background: 'rgba(0,0,0,0.72)',
          backdropFilter: 'blur(8px)',
          borderColor: 'rgba(255,255,255,0.12)',
        }}
      >
        <Button
          startIcon={<BackIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate('/events/v2')}
        >
          Back to Events
        </Button>
      </Box>
    </Box>
  )
}

export default EventDetailV2Page
